/**
 * `GET /v1/accounts/{account_ref}/balance` — what is in one account (#140).
 *
 * Until `go-wallet#972` landed, the customer surface published a balance
 * nowhere: `GET /v1/accounts` says which accounts somebody holds and carries no
 * figure, deliberately. So this app could show WHICH accounts a customer held
 * and not WHAT WAS IN THEM, and Overview filled the gap from `fixtures.ts` —
 * invented money under a real customer's name.
 *
 * # `available` is not `balance`, and the difference is the point
 *
 * `available = min(balance − pending_out, the institution's own figure)`, and
 * the backend computes it with the same code the money path uses rather than as
 * a second projection — a display figure computed a second way would eventually
 * disagree with the one that governs a cash-out, and the customer would be
 * shown money they cannot spend.
 *
 * So this app never recomputes it. `capped_by` says which operand bound the
 * result, which is what lets a screen explain the gap without arithmetic of its
 * own — and note the institution's raw figure is deliberately NOT published: a
 * customer can act on "some of this is reserved against a withdrawal you
 * already asked for" and can do nothing with the institution's internal number.
 *
 * # The figure is not live and must not be shown as though it were
 *
 * `providerAsOf` is the instant of the institution's snapshot, not of the
 * response. Past its staleness bound the backend REFUSES rather than falling
 * back to our own figure, because falling back defeats the cap at exactly the
 * moment it matters.
 *
 * # One answer for three causes
 *
 * `503 balance_unavailable` covers a stale snapshot, a missing wallet and a
 * read failure as ONE answer on purpose: distinguishing them would publish the
 * institution's posture to anyone who can hold an account. This module does not
 * try to tell them apart, and nothing downstream should either.
 */
import { moneyFromDTO } from '@/lib/money'
import type { Money } from '@/lib/money'
import type { WalletApi } from '@/lib/api'

/** Which operand bound `available`. */
export const CAPPED_BY = ['ledger', 'provider'] as const
export type CappedBy = (typeof CAPPED_BY)[number]

export interface Balance {
  readonly accountRef: string
  /**
   * The whole position on our own book, wallet AND hold.
   *
   * A reservation is (wallet −H, hold +H), so the wallet leg alone is already
   * net of holds; the contract publishes the combined figure precisely so a
   * client cannot subtract them twice.
   */
  readonly balance: Money
  /** The part of `balance` reserved against an instruction already given. */
  readonly pendingOut: Money
  /** What may actually be spent. Never recomputed here. */
  readonly available: Money
  readonly cappedBy: CappedBy
  /** When the institution's operand was taken. NOT when this response was made. */
  readonly providerAsOf: string
}

/**
 * One account's balance, or why there isn't one.
 *
 * `unavailable` carries no cause, matching the route: see above.
 */
export type BalanceState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly balance: Balance }
  /** We could not get the figure. NEVER rendered as zero. */
  | { readonly status: 'unavailable'; readonly reason: string }

interface BalanceDTO {
  account_ref?: unknown
  balance?: unknown
  pending_out?: unknown
  available?: unknown
  capped_by?: unknown
  provider_as_of?: unknown
}

function isCappedBy(v: unknown): v is CappedBy {
  return typeof v === 'string' && (CAPPED_BY as readonly string[]).includes(v)
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}

/**
 * Decode one balance, or say what was wrong with it.
 *
 * Every one of the six fields the contract marks required is checked, and none
 * is defaulted. A missing `pending_out` read as zero would tell somebody money
 * is spendable that is already committed, which is the one mistake on this
 * surface that costs the customer rather than the app.
 */
export function decodeBalance(dto: BalanceDTO): Balance | string {
  const accountRef = str(dto.account_ref)
  if (accountRef === undefined) return 'a balance arrived with no account reference'

  let balance: Money
  let pendingOut: Money
  let available: Money
  try {
    // `moneyFromDTO` enforces invariants 3 and 4: quoted-integer minor units, a
    // required ISO-4217 currency, and membership of money.ts's closed exponent
    // table. Unlike `decodeAccount` — which deliberately tolerates a currency
    // it cannot name, because an account carries no amount — a figure with an
    // unknown scale is refused here. Rendering it would be a factor of ten out
    // and would look fine (#93).
    balance = moneyFromDTO(dto.balance as never)
    pendingOut = moneyFromDTO(dto.pending_out as never)
    available = moneyFromDTO(dto.available as never)
  } catch (err) {
    return `the balance for ${accountRef} could not be read (${err instanceof Error ? err.message : String(err)})`
  }

  // Three figures on one account must be one currency. A mismatch is a backend
  // fault and there is no safe way to render it: any total would be a
  // cross-currency sum, which invariant 3 forbids outright.
  //
  // Written as a set rather than as `a !== b || a !== c`, which
  // `check-invariants.sh` reads — correctly, as a pattern — as a `||` fallback
  // for a currency. Rephrasing beats exempting: an exemption outlives the
  // reason for it, and this says the same thing more plainly anyway.
  if (new Set([balance.currency, pendingOut.currency, available.currency]).size !== 1) {
    return `the balance for ${accountRef} mixes currencies, and no figure on it can be trusted`
  }

  if (!isCappedBy(dto.capped_by)) {
    return `the backend reported a cap this app does not know (${String(dto.capped_by)})`
  }

  const providerAsOf = str(dto.provider_as_of)
  if (providerAsOf === undefined) {
    // Not defaulted to "now". The whole point of the field is that the figure
    // is older than the response, and inventing a timestamp would say it is not.
    return `the balance for ${accountRef} arrived without saying when it was taken`
  }

  return { accountRef, balance, pendingOut, available, cappedBy: dto.capped_by, providerAsOf }
}

export async function fetchBalance(api: WalletApi, accountRef: string): Promise<BalanceState> {
  let dto: BalanceDTO
  try {
    dto = await api.get<BalanceDTO>(`/v1/accounts/${encodeURIComponent(accountRef)}/balance`)
  } catch (err) {
    return { status: 'unavailable', reason: err instanceof Error ? err.message : String(err) }
  }
  const decoded = decodeBalance(dto ?? {})
  if (typeof decoded === 'string') return { status: 'unavailable', reason: decoded }
  return { status: 'ready', balance: decoded }
}

/**
 * How old a snapshot may be before a screen says so.
 *
 * A threshold in this app is a PRESENTATION choice and never a safety one: the
 * backend already refuses a figure past its own staleness bound, so anything
 * that arrives here is within it. This only decides when to mention the age.
 */
export const STALE_AFTER_MS = 15 * 60 * 1000

export function isStale(providerAsOf: string, now: Date): boolean {
  const taken = Date.parse(providerAsOf)
  if (Number.isNaN(taken)) return false
  return now.getTime() - taken > STALE_AFTER_MS
}
